import React, { FC, useRef } from 'react';
import {
  MediaCategory,
  useCreateMediaFileMutation,
} from '../../../generated/graphql';
import toast from 'react-hot-toast';
import ToastAlert from '../ToastAlert';
import IconButton from '../IconButton';
import { PhotographIcon } from '@heroicons/react/outline';
import { validateImageFile } from '../../utils/fileValidation';
import { useChatImageUploadConstants } from '../../hooks/useUIConstants';

export type MediaInfo = { url: string; id: string };

const ImageUploadButton: FC<{ onSuccess: (info: MediaInfo) => void }> = ({
  onSuccess,
}) => {
  const [createMediaFile, { loading }] = useCreateMediaFileMutation();
  const {
    maxChatImageSize,
    acceptedChatImageTypes,
    acceptedChatImageExtensions,
  } = useChatImageUploadConstants();

  const fileInputRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const onFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const image = e.target.files?.[0];

    if (image) {
      // Validate file before upload
      if (
        !validateImageFile(image, {
          maxFileSize: maxChatImageSize,
          acceptedImageTypes: acceptedChatImageTypes,
        })
      ) {
        return;
      }

      try {
        const data = await createMediaFile({
          variables: {
            input: {
              file: image,
              category: MediaCategory.ModuleImage,
            },
          },
        });

        onSuccess(data.data?.createMediaFile);
      } catch (err) {
        toast.custom(({ visible }) => (
          <ToastAlert
            isVisible={visible}
            message="Something went wrong."
            level="error"
          />
        ));
      }
    }
  };

  return (
    <>
      <IconButton
        aria-label="Upload image"
        IconComponent={PhotographIcon}
        className="h-10 w-10 justify-center text-neutral-125"
        iconClassName="h-6 w-6 shrink-0 stroke-neutral-125"
        disabled={loading}
        onClick={() => {
          fileInputRef.current?.click();
        }}
      />
      {!loading && (
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedChatImageExtensions.join(',')}
          className="hidden"
          onChange={onFileUpload}
        />
      )}
    </>
  );
};

export default ImageUploadButton;
